import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { FeatureFlag } from '@pflegenavi/shared/api';

interface UserEntity {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  locale?: string;
  created_at?: string;
}

export interface EmployeeProfilePhoenix {
  user: UserEntity;
  resource_associations: {
    [key: string]: Array<{
      resource_id: string;
      role: {
        name: 'admin' | 'nursing-home-manager' | 'employee';
        description: string;
      };
    }>;
  };
  use_new_authorization: boolean;
  feature_flags: FeatureFlag[];
}

// Phoenix-specific DTOs that match the Phoenix API format
export type FamilyMemberRelationshipPhoenix =
  | 'resident'
  | 'family_member'
  | 'legal_guardian'
  | 'other';

export interface UpdateFamilyMemberPhoenixDto {
  resident_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  locale?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  zip_code?: string;
  notes?: string;
  relationship_to_resident?: FamilyMemberRelationshipPhoenix;
}

export interface UpdateFamilyMemberPhoenixResponse {
  status: string;
  message: string;
}

export type CardType = 'PHYSICAL' | 'VIRTUAL';

export type CardStatus =
  | 'PENDING'
  | 'PENDING_ACTIVATION'
  | 'PENDING_ISSUANCE'
  | 'PENDING_REGISTRATION'
  | 'PENDING_VALIDITY'
  | 'REQUEST_APPROVED'
  | 'REQUESTED'
  | 'REQUEST_CANCELLED'
  | 'REQUEST_REJECTED'
  | 'ACTIVE'
  | 'LOCKED'
  | 'LOCKED_PIN'
  | 'EXPIRED'
  | 'TERMINATED'
  | 'TERMINATED_LOST'
  | 'TERMINATED_STOLEN';

export type LimitRenewFrequency =
  | 'DAILY'
  | 'WEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'ANNUAL'
  | 'TOTAL';

export interface CashWithdrawalLimits {
  max_per_transaction: number;
  min_amount: number;
  daily_limit: number;
  four_day_limit: number;
  daily_frequency: number;
  four_day_frequency: number;
}

export interface EmployeeCard {
  id: string;
  cardholder_id: string;
  organization_id: string;
  organization_name: string;
  card_type: CardType;
  card_status: CardStatus;
  last_four_digits: string;
  issued_at: string;
  expires_at: string;
  terminated_at: string | null;
  transaction_limit: number;
  transaction_limit_currency: string;
  spending_balance: number;
  spending_balance_currency: string;
  available_balance: number;
  available_balance_currency: string;
  total_limit: number;
  total_limit_currency: string;
  limit_period_end: string | null;
  limit_renew_date: string | null;
  limit_renew_frequency: LimitRenewFrequency | null;
  cash_withdrawal_limits: CashWithdrawalLimits;
}

export interface EmployeeCardsResponse {
  data: EmployeeCard[];
}

export type EmployeeCardWithDates = Omit<
  EmployeeCard,
  | 'issued_at'
  | 'expires_at'
  | 'terminated_at'
  | 'limit_renew_date'
  | 'limit_period_end'
> & {
  issued_at: Date;
  expires_at: Date;
  terminated_at: Date | null;
  limit_renew_date: Date | null;
  limit_period_end: Date | null;
};

export type EmployeeCardsResponseWithDates = Omit<
  EmployeeCardsResponse,
  'data'
> & {
  data: EmployeeCardWithDates[];
};

export interface IUserProfilePhoenixApi extends Api {
  getEmployeeProfile(): Promise<EmployeeProfilePhoenix>;
  getEmployeeCards(): Promise<EmployeeCardsResponse>;
  updateFamilyMemberByEmployee(dataIn: {
    params: { familyMemberId: string };
    body: UpdateFamilyMemberPhoenixDto;
  }): Promise<UpdateFamilyMemberPhoenixResponse>;
}

export class UserProfilePhoenixApi implements IUserProfilePhoenixApi {
  baseUrl: string;

  getEmployeeProfile: IUserProfilePhoenixApi['getEmployeeProfile'];
  getEmployeeCards: IUserProfilePhoenixApi['getEmployeeCards'];
  updateFamilyMemberByEmployee: IUserProfilePhoenixApi['updateFamilyMemberByEmployee'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.getEmployeeProfile = get({
      authContext,
      url: `${this.baseUrl}/employee-profile`,
    });

    this.getEmployeeCards = get({
      authContext,
      url: `${this.baseUrl}/employee-profile/cards`,
    });

    this.updateFamilyMemberByEmployee = modify<
      UpdateFamilyMemberPhoenixDto,
      UpdateFamilyMemberPhoenixResponse,
      UpdateFamilyMemberPhoenixResponse,
      { familyMemberId: string }
    >({
      authContext,
      url: ({ familyMemberId }) =>
        `${this.baseUrl}/family-member/user-profile/${familyMemberId}`,
      method: 'PUT',
    });
  }

  // eslint-disable-next-line class-methods-use-this
  get headers(): Headers {
    return new Headers({
      'content-type': 'application/json',
    });
  }
}
